import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import './AddRechnungWizard.css';
import './SendEmailWizard.css';
import { RoundTable } from './wizardTypes';
import EmailWizardStepIndicator from './EmailWizardStepIndicator';
import EmailWizardStep1 from './EmailWizardStep1';
import EmailWizardStep2 from './EmailWizardStep2';
import EmailWizardStep3 from './EmailWizardStep3';

interface SendEmailWizardProps {
    projectId: number;
    projectName: string;
    onClose: () => void;
    token: string;
}

interface Rechnung {
    id: number;
    roundTable?: {
        id: number;
        name: string;
        number: number;
    };
}

interface RoundTableWithInvoice extends RoundTable {
    rechnungId: number;
    selected: boolean;
}

interface SendProgress {
    current: number;
    total: number;
}

interface EmailResult {
    roundTableId: number;
    roundTableName?: string;
    success: boolean;
    error?: string;
    attachmentWarning?: string;
}

interface SendResults {
    message: string;
    totalRecipients: number;
    successCount: number;
    failureCount: number;
    warningsCount?: number;
    results: EmailResult[];
}

const SendEmailWizard: React.FC<SendEmailWizardProps> = ({
    projectId,
    projectName,
    onClose,
    token,
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [error, setError] = useState<string | null>(null);

    // Step 1: Table Selection
    const [roundTablesWithInvoices, setRoundTablesWithInvoices] = useState<RoundTableWithInvoice[]>([]);
    const [loadingTables, setLoadingTables] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Step 2: Email Composition
    const [subject, setSubject] = useState('');
    const [cc, setCc] = useState('');
    const [content, setContent] = useState('');
    const [attachInvoice, setAttachInvoice] = useState(true);

    // Step 3: Sending state
    const [isSending, setIsSending] = useState(false);
    const [sendProgress, setSendProgress] = useState<SendProgress>({ current: 0, total: 0 });
    const [sendResults, setSendResults] = useState<SendResults | null>(null);

    // Ref for textarea to track cursor position
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Fetch round tables with invoices
    useEffect(() => {
        const fetchRoundTablesWithInvoices = async () => {
            try {
                // First fetch all round tables
                const rtResponse = await fetch('/api/roundtables', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!rtResponse.ok) {
                    throw new Error('Failed to fetch round tables');
                }

                const allRoundTables: RoundTable[] = await rtResponse.json();

                // Then fetch invoices for this project
                const rechnungenResponse = await fetch(`/api/rechnungen/project/${projectId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!rechnungenResponse.ok) {
                    throw new Error('Failed to fetch invoices');
                }

                const rechnungen: Rechnung[] = await rechnungenResponse.json();

                // Filter round tables that have invoices and map them
                // Use a Map to avoid duplicates if multiple invoices exist for the same round table
                const tablesMap = new Map<number, RoundTableWithInvoice>();
                
                rechnungen.forEach(rechnung => {
                    if (rechnung.roundTable) {
                        const roundTableId = rechnung.roundTable.id;
                        // Only add if not already in the map
                        if (!tablesMap.has(roundTableId)) {
                            const fullTable = allRoundTables.find(rt => rt.id === roundTableId);
                            if (fullTable) {
                                tablesMap.set(roundTableId, {
                                    ...fullTable,
                                    rechnungId: rechnung.id,
                                    selected: true // All selected by default
                                });
                            }
                        }
                    }
                });

                setRoundTablesWithInvoices(Array.from(tablesMap.values()));
            } catch (error) {
                console.error('Error fetching round tables with invoices:', error);
                toast.error('Fehler beim Laden der Round Tables');
            } finally {
                setLoadingTables(false);
            }
        };

        fetchRoundTablesWithInvoices();
    }, [token, projectId]);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const toggleTableSelection = (tableId: number) => {
        setRoundTablesWithInvoices(tables =>
            tables.map(t => t.id === tableId ? { ...t, selected: !t.selected } : t)
        );
    };

    const handleNext = () => {
        setError(null);

        if (currentStep === 1) {
            const selectedCount = roundTablesWithInvoices.filter(t => t.selected).length;
            if (selectedCount === 0) {
                setError('Bitte wählen Sie mindestens einen Round Table aus');
                return;
            }
            setCurrentStep(2);
        } else if (currentStep === 2) {
            if (!subject.trim()) {
                setError('Bitte geben Sie einen Betreff ein');
                return;
            }
            if (!content.trim()) {
                setError('Bitte geben Sie einen Inhalt ein');
                return;
            }
            setCurrentStep(3);
        }
    };

    const handleBack = () => {
        setError(null);
        if (currentStep === 2) {
            setCurrentStep(1);
        } else if (currentStep === 3) {
            setCurrentStep(2);
        }
    };

    const handleSendTest = async () => {
        // Send test email to the first selected round table
        const selectedTables = roundTablesWithInvoices.filter(t => t.selected);
        if (selectedTables.length === 0) {
            toast.error('Keine Round Tables ausgewählt');
            return;
        }

        setIsSending(true);
        setSendProgress({ current: 0, total: 1 });

        try {
            const testRecipient = selectedTables[0];
            const response = await fetch('/api/mails/send-project-emails', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    projectId,
                    recipients: [{
                        roundTableId: testRecipient.id,
                        rechnungId: testRecipient.rechnungId
                    }],
                    subject,
                    cc,
                    content,
                    attachInvoice
                })
            });

            setSendProgress({ current: 1, total: 1 });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Fehler beim Senden der Test-E-Mail');
            }

            const result = await response.json();
            
            if (result.successCount > 0) {
                if (result.results[0]?.attachmentWarning) {
                    toast.warning(`Test-E-Mail versendet, aber Anhang konnte nicht hinzugefügt werden: ${result.results[0].attachmentWarning}`);
                } else {
                    toast.success('Test-E-Mail erfolgreich versendet!');
                }
            } else {
                toast.error(`Test-E-Mail konnte nicht versendet werden: ${result.results[0]?.error || 'Unbekannter Fehler'}`);
            }
        } catch (error: any) {
            console.error('Error sending test email:', error);
            toast.error(error.message || 'Fehler beim Senden der Test-E-Mail');
        } finally {
            setIsSending(false);
            setSendProgress({ current: 0, total: 0 });
        }
    };

    const handleSkipTest = async () => {
        // Send emails to all selected round tables
        const selectedTables = roundTablesWithInvoices.filter(t => t.selected);
        if (selectedTables.length === 0) {
            toast.error('Keine Round Tables ausgewählt');
            return;
        }

        setIsSending(true);
        setSendProgress({ current: 0, total: selectedTables.length });

        let progressInterval: NodeJS.Timeout | null = null;

        try {
            const recipients = selectedTables.map(table => ({
                roundTableId: table.id,
                rechnungId: table.rechnungId
            }));

            // We'll simulate progress updates since the backend processes sequentially
            // The backend adds a 1 second delay between emails
            progressInterval = setInterval(() => {
                setSendProgress(prev => {
                    if (prev.current < prev.total) {
                        return { ...prev, current: prev.current + 1 };
                    }
                    return prev;
                });
            }, 1200); // Slightly more than 1 second to account for processing time

            const response = await fetch('/api/mails/send-project-emails', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    projectId,
                    recipients,
                    subject,
                    cc,
                    content,
                    attachInvoice
                })
            });

            setSendProgress({ current: selectedTables.length, total: selectedTables.length });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Fehler beim Versenden der E-Mails');
            }

            const result = await response.json();
            setSendResults(result);

            if (result.successCount === result.totalRecipients) {
                if (result.warningsCount && result.warningsCount > 0) {
                    toast.warning(`Alle ${result.successCount} E-Mails versendet, aber ${result.warningsCount} Anhang(e) konnten nicht hinzugefügt werden.`);
                } else {
                    toast.success(`Alle ${result.successCount} E-Mails erfolgreich versendet!`);
                }
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else if (result.successCount > 0) {
                const warningText = result.warningsCount && result.warningsCount > 0 
                    ? ` ${result.warningsCount} Anhang(e) fehlten.`
                    : '';
                toast.warning(`${result.successCount} von ${result.totalRecipients} E-Mails versendet. ${result.failureCount} fehlgeschlagen.${warningText}`);
            } else {
                toast.error('Keine E-Mails konnten versendet werden.');
            }
        } catch (error: any) {
            console.error('Error sending emails:', error);
            toast.error(error.message || 'Fehler beim Versenden der E-Mails');
        } finally {
            if (progressInterval) {
                clearInterval(progressInterval);
            }
            setIsSending(false);
        }
    };

    const insertPlaceholder = (placeholder: string) => {
        const textarea = textareaRef.current;
        if (!textarea) {
            // Fallback: append to end if textarea ref is not available
            setContent(prev => prev + placeholder);
            return;
        }

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        // Insert placeholder at cursor position
        const newContent = content.substring(0, start) + placeholder + content.substring(end);
        setContent(newContent);

        // Set cursor position after the inserted placeholder
        // Use setTimeout to ensure React state update completes before setting cursor
        setTimeout(() => {
            if (textarea) {
                const newCursorPos = start + placeholder.length;
                textarea.focus();
                textarea.setSelectionRange(newCursorPos, newCursorPos);
            }
        }, 0);
    };

    return (
        <div className="wizard-overlay" onClick={handleOverlayClick}>
            <div className="wizard-modal wizard-modal-large">
                <div className="wizard-header">
                    <h2>E-Mails versenden</h2>
                    <button
                        className="wizard-close-btn"
                        onClick={onClose}
                        aria-label="Schließen"
                    >
                        ✕
                    </button>
                </div>

                <div className="wizard-content">
                    <div className="wizard-info">
                        <p className="project-info">
                            <strong>Projekt:</strong> {projectName}
                        </p>
                    </div>

                    <EmailWizardStepIndicator currentStep={currentStep} />

                    <div className="wizard-steps">
                        {currentStep === 1 && (
                            <EmailWizardStep1
                                roundTablesWithInvoices={roundTablesWithInvoices}
                                loadingTables={loadingTables}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                toggleTableSelection={toggleTableSelection}
                            />
                        )}
                        {currentStep === 2 && (
                            <EmailWizardStep2
                                subject={subject}
                                setSubject={setSubject}
                                cc={cc}
                                setCc={setCc}
                                content={content}
                                setContent={setContent}
                                attachInvoice={attachInvoice}
                                setAttachInvoice={setAttachInvoice}
                                insertPlaceholder={insertPlaceholder}
                                textareaRef={textareaRef}
                            />
                        )}
                        {currentStep === 3 && (
                            <EmailWizardStep3
                                roundTablesWithInvoices={roundTablesWithInvoices}
                                subject={subject}
                                cc={cc}
                                content={content}
                                attachInvoice={attachInvoice}
                                onSendTest={handleSendTest}
                                onSkipTest={handleSkipTest}
                                isSending={isSending}
                                sendProgress={sendProgress}
                                sendResults={sendResults}
                            />
                        )}
                    </div>

                    {error && (
                        <div className="wizard-error">
                            {error}
                        </div>
                    )}

                    <div className="wizard-actions">
                        <button
                            type="button"
                            className="wizard-btn wizard-btn-cancel"
                            onClick={onClose}
                        >
                            Abbrechen
                        </button>
                        {currentStep > 1 && currentStep < 3 && (
                            <button
                                type="button"
                                className="wizard-btn wizard-btn-back"
                                onClick={handleBack}
                            >
                                Zurück
                            </button>
                        )}
                        {currentStep < 3 && (
                            <button
                                type="button"
                                className="wizard-btn wizard-btn-submit"
                                onClick={handleNext}
                            >
                                Weiter
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SendEmailWizard;
